import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Popover, Progress, Space, Tag } from "antd";
import { ETranslationFlag } from "@devowl-wp/api";
import { DEFAULT_LANGUAGE } from "@devowl-wp/api-real-cookie-banner";
import { colors } from "../../../../styles/colors.js";
import { spacing } from "../../../../styles/spacing.js";
import { getLanguageIcon } from "../../../../util/translation.js";
import { TranslationStatus } from "../template/fields/translation/status/translation-status.js";
function useStatisticsColumns(t) {
    return [
        {
            title: t("Country"),
            dataIndex: "icon",
            render (_, record) {
                return getLanguageIcon(record.language, {
                    width: "1.5em",
                    height: "1.5em"
                });
            }
        },
        {
            title: t("Language"),
            dataIndex: "label"
        },
        {
            title: t("Words translatable"),
            dataIndex: "translatable",
            align: "right"
        },
        {
            title: t("Words translated"),
            dataIndex: "translated",
            align: "right"
        },
        {
            title: t("Words not translated"),
            dataIndex: "notTranslated",
            align: "right"
        },
        {
            title: t("Fuzzy translations"),
            dataIndex: "fuzzy",
            align: "right"
        },
        {
            title: t("Machine translated"),
            dataIndex: "machineTranslated",
            align: "right"
        },
        {
            title: t("Translation status"),
            dataIndex: "statusInPercent",
            render (percentage) {
                return /*#__PURE__*/ _jsx(Progress, {
                    style: {
                        width: "100%",
                        margin: spacing.layout.content.marginMedium
                    },
                    percent: Math.floor(percentage),
                    strokeColor: colors.component.progress.strokeColors,
                    size: "small",
                    status: percentage < 100 ? "normal" : "success"
                });
            }
        }
    ];
}
function useStatisticsExtraColumns(tableData, t, templateType) {
    const languageIcon = getLanguageIcon(tableData.language, {
        height: "1.4em",
        width: "1.4em"
    });
    const generateFilters = ()=>{
        return Object.keys(ETranslationFlag).map((flagKey)=>({
                text: flagKey.replace(/([A-Z])/g, " $1").trim(),
                value: flagKey
            }));
    };
    const extraColumns = [
        {
            title: t("Translation status"),
            dataIndex: "translationStatus",
            width: "10rem",
            render: (_, record)=>{
                const foundInfo = record.translationInfo?.find((info)=>info.language === tableData.language);
                let isComplete = undefined;
                if (foundInfo) {
                    if (Object.hasOwn(foundInfo, "isTranslationCompleteRecursive")) {
                        isComplete = foundInfo.isTranslationCompleteRecursive;
                    } else {
                        isComplete = foundInfo.isTranslationComplete;
                    }
                }
                const machineTranslationStatus = foundInfo?.machineTranslationStatus;
                const defaultLangStatus = record.translationInfo.find((info)=>info.language === DEFAULT_LANGUAGE);
                return /*#__PURE__*/ _jsx("div", {
                    children: /*#__PURE__*/ _jsx(Popover, {
                        title: t("Translation status"),
                        autoAdjustOverflow: true,
                        content: /*#__PURE__*/ _jsx(TranslationStatus, {
                            defaultLanguageStatus: defaultLangStatus,
                            targetStatus: foundInfo,
                            language: tableData.language,
                            templateType: templateType,
                            template: record,
                            machineTranslationStatus: machineTranslationStatus
                        }),
                        trigger: "hover",
                        placement: "left",
                        children: /*#__PURE__*/ _jsx("div", {
                            style: {
                                opacity: !isComplete ? 0.5 : 1,
                                padding: "0.1rem"
                            },
                            children: languageIcon
                        })
                    })
                }, `${record.id}-languages-info`);
            },
            onFilter: (value, record)=>{
                const foundInfo = record.translationInfo?.find((info)=>info.language === tableData.language);
                let isComplete = false;
                if (!isComplete && foundInfo) {
                    if (Object.hasOwn(foundInfo, "isTranslationCompleteRecursive")) {
                        isComplete = foundInfo.isTranslationCompleteRecursive;
                    } else {
                        isComplete = foundInfo.isTranslationComplete;
                    }
                }
                return (isComplete ? 1 : 0) === value;
            },
            filterMultiple: false,
            filters: [
                {
                    text: t("Complete"),
                    value: 1
                },
                {
                    text: t("Incomplete"),
                    value: 0
                }
            ]
        },
        {
            dataIndex: "flags",
            title: t("Flags"),
            filters: generateFilters(),
            onFilter: (value, record)=>{
                const targetInfo = record.translationInfo.find((info)=>info.language === tableData.language);
                if (targetInfo?.translationFlaggedFields) {
                    const flag = value;
                    const flagged = targetInfo.translationFlaggedFields[flag];
                    return flagged?.length > 0;
                } else {
                    return value === undefined;
                }
            },
            render: (_, record)=>{
                const targetInfo = record.translationInfo.find((info)=>info.language === tableData.language);
                const hasFuzzy = targetInfo?.translationFlaggedFields?.Fuzzy?.length > 0;
                const hasMachineTranslatedCount = targetInfo?.translationFlaggedFields?.MachineTranslated?.length > 0;
                return /*#__PURE__*/ _jsxs(Space, {
                    size: [
                        8,
                        8
                    ],
                    wrap: true,
                    children: [
                        hasFuzzy && /*#__PURE__*/ _jsx(Tag, {
                            color: colors.translation.fuzzy,
                            children: t("Fuzzy")
                        }),
                        hasMachineTranslatedCount && /*#__PURE__*/ _jsx(Tag, {
                            color: colors.translation.machineTranslated,
                            children: t("Machine-Translated")
                        }),
                        !hasFuzzy && !hasMachineTranslatedCount && /*#__PURE__*/ _jsx(Tag, {
                            color: "grey",
                            children: t("None")
                        })
                    ]
                });
            }
        }
    ];
    return extraColumns;
}
export { useStatisticsColumns, useStatisticsExtraColumns };
